<script lang="ts" setup>
import { computed, provide, ref } from 'vue';
import { isNil } from 'lodash';
import { useI18n } from 'vue-i18n';

import { AssetContainerEventType, AssetContainerState, YardTaskStatus } from '@nova/core';
import {
  isLatestCheckMissing as computeIsLatestCheckMissing,
  filterCheckOutcomesByAsset
} from '@/helpers/latestCheckOutcome';
import { CUSTOM_FORMS_STORAGE_CONTEXT_KEY } from '@satellite/composables/useCustomFormsStorageContext';
import {
  getAssetContainerStatusIcon,
  getAssetContainerStatusColor,
  getAssetContainerStateIcon
} from '@satellite/helpers/yardTaskUtils';
import { PRESENTER_VALUE_FALLBACK } from '@satellite/components/elements/presenters/presenterValueFallback';
import { useYardTaskPresenterV2 } from '@satellite/composables/useYardTaskPresenterV2';

import { useWarehouseStore } from '@/store/useWarehouseStore';
import { useCheckAssetPanel } from '@/composables/useCheckAssetPanel';
import { useCheckTaskPanel } from '@/composables/useCheckTaskPanel';
import {
  useAssetCheckWizard,
  WIZARD_STATUS_OPTIONS,
  WIZARD_SKIP,
  WIZARD_STEPS
} from '@/composables/useAssetCheckWizard';
import { relatedObjectToYardAsset } from '@/helpers/checkTaskDetailsTitle';
import AssetCheckPanelHeader from '@/components/AssetCheckPanelHeader.vue';
import AssetCheckPanelLocationBar from '@/components/AssetCheckPanelLocationBar.vue';
import AssetCheckPanelCheckSummary from '@/components/AssetCheckPanelCheckSummary.vue';
import AssetCheckPanelDetails from '@/components/AssetCheckPanelDetails.vue';
import AssetCheckPanelFooter from '@/components/AssetCheckPanelFooter.vue';
import AssetCheckWizardStepper from '@/components/AssetCheckWizardStepper.vue';
import AssetCheckWizardStep1 from '@/components/AssetCheckWizardStep1.vue';
import AssetCheckWizardStep2 from '@/components/AssetCheckWizardStep2.vue';
import AssetCheckWizardStep3 from '@/components/AssetCheckWizardStep3.vue';

import type { CheckTaskCheckAssetContainerView, CheckTaskViewData, Nullable } from '@nova/core';

const { t } = useI18n();

const warehouseStore = useWarehouseStore();
provide(
  CUSTOM_FORMS_STORAGE_CONTEXT_KEY,
  computed(() => ({
    warehouseId: warehouseStore.selectedWarehouseId ?? '',
    storageEndpoint: 'storage'
  }))
);

const { dialogProps, handleAfterLeave, buildControls } =
  useCheckTaskPanel('open-asset-check-wizard');

const assetId = computed((): Nullable<string> => dialogProps.value?.assetContainerId ?? null);
const taskId = computed((): Nullable<string> => dialogProps.value?.taskId ?? null);

const taskPresenter = useYardTaskPresenterV2({ taskId, headless: true });
const liveTask = computed<CheckTaskViewData | null>(
  () => (taskPresenter.data.value as CheckTaskViewData | undefined) ?? null
);
const checkAssetContainers = computed<CheckTaskCheckAssetContainerView[] | undefined>(() => {
  // Both the live task and the event payload can carry every asset's outcomes; narrow to the
  // current asset so an unchecked asset never inherits another asset's check (OD-3837 A2/A3).
  const source = liveTask.value?.checkAssetContainers ?? dialogProps.value?.checkAssetContainers;
  return source ? filterCheckOutcomesByAsset(source, assetId.value) : source;
});

const initialAssetData = computed(() => {
  const id = dialogProps.value?.assetContainerId;
  if (isNil(id)) {
    return null;
  }
  return relatedObjectToYardAsset({ id }) ?? null;
});

const hasExistingCheck = computed((): boolean => (checkAssetContainers.value?.length ?? 0) > 0);

const isLatestCheckMissing = computed((): boolean =>
  computeIsLatestCheckMissing(checkAssetContainers.value)
);

const isTaskCompleted = computed(() => dialogProps.value?.taskStatus === YardTaskStatus.COMPLETED);

const { asset, assetCode, spotCode, areaName, isLoading, confirmDisabled, onIntersect } =
  useCheckAssetPanel({ assetId, initialAssetData });

const { isOpen, closePanel } = buildControls(onIntersect);

const isCheckMode = ref(false);

function finishCheck(): void {
  isCheckMode.value = false;
}

const {
  state: {
    step,
    selectedStatus,
    selectedState,
    locationOutcomeLabel,
    isMutatingStep,
    isReassigningSpot,
    stepErrors
  },
  mutations: { setStatus, setState, reset: resetWizard },
  workflows: { submitMissing, confirmLocation, openSpotPicker, goToStep3, submitComplete }
} = useAssetCheckWizard({
  dialogProps,
  asset,
  finishCheck,
  locationConfirmedLabel: t('assetCheckWizard.step1.locationConfirmed'),
  locationUpdatedLabel: t('assetCheckWizard.step1.updateLocation')
});

function enterCheckMode(): void {
  resetWizard();
  isCheckMode.value = true;
}

function selectStatus(key: string): void {
  setStatus(key);
  goToStep3();
}

function selectState(key: string): void {
  setState(key);
  submitComplete();
}

function onAfterLeave(): void {
  isCheckMode.value = false;
  resetWizard();
  handleAfterLeave();
}

const headerTitle = computed((): string => assetCode.value ?? PRESENTER_VALUE_FALLBACK);

const companyName = computed(
  () =>
    asset.value?.assetContainerEvents?.[0]?.assetVisit?.companyHint ??
    asset.value?.company?.name ??
    null
);
const isHooked = computed(() => Boolean(asset.value?.assetVisitId));

const STATUS_I18N_MAP: Record<(typeof WIZARD_STATUS_OPTIONS)[number], string> = {
  [AssetContainerEventType.Arrived]: 'assetCheckWizard.step2.arrived',
  [AssetContainerEventType.Loading]: 'assetCheckWizard.step2.loading',
  [AssetContainerEventType.LoadingComplete]: 'assetCheckWizard.step2.loadingComplete',
  [AssetContainerEventType.Unloading]: 'assetCheckWizard.step2.unloading',
  [AssetContainerEventType.UnloadingComplete]: 'assetCheckWizard.step2.unloadingComplete',
  [AssetContainerEventType.ReadyForPickup]: 'assetCheckWizard.step2.readyForPickup',
  [AssetContainerEventType.Inoperable]: 'assetCheckWizard.step2.inoperable'
};

const statusOptions = computed(() => [
  ...WIZARD_STATUS_OPTIONS.map(key => ({
    key,
    label: t(STATUS_I18N_MAP[key]),
    icon: getAssetContainerStatusIcon(key),
    color: getAssetContainerStatusColor(key)
  })),
  {
    key: WIZARD_SKIP,
    label: t('assetCheckWizard.notRequiredForCheck'),
    icon: 'mdi-close',
    color: undefined
  }
]);

const STATE_I18N_MAP: Record<AssetContainerState, string> = {
  [AssetContainerState.Full]: 'assetCheckWizard.step3.full',
  [AssetContainerState.Empty]: 'assetCheckWizard.step3.empty',
  [AssetContainerState.PartiallyLoaded]: 'assetCheckWizard.step3.partiallyLoaded'
};

const stateOptions = computed(() => [
  ...[AssetContainerState.Full, AssetContainerState.Empty, AssetContainerState.PartiallyLoaded].map(
    key => ({
      key,
      label: t(STATE_I18N_MAP[key]),
      icon: getAssetContainerStateIcon(key),
      color: undefined
    })
  ),
  {
    key: WIZARD_SKIP,
    label: t('assetCheckWizard.notRequiredForCheck'),
    icon: 'mdi-close',
    color: undefined
  }
]);

const stepperSubtitles = computed(() => ({
  step1: locationOutcomeLabel.value ?? undefined,
  step2: selectedStatus.value
    ? statusOptions.value.find(o => o.key === selectedStatus.value)?.label
    : undefined,
  step3: selectedState.value
    ? stateOptions.value.find(o => o.key === selectedState.value)?.label
    : undefined
}));

const step1ErrorMessage = computed(() => {
  const err = stepErrors.value[WIZARD_STEPS.LOCATION];
  return err ? t(`assetCheckWizard.errors.${err}`) : null;
});

const step2ErrorMessage = computed(() => {
  const err = stepErrors.value[WIZARD_STEPS.STATUS];
  return err ? t(`assetCheckWizard.errors.${err}`) : null;
});

const step3ErrorMessage = computed(() => {
  const err = stepErrors.value[WIZARD_STEPS.STATE];
  return err ? t(`assetCheckWizard.errors.${err}`) : null;
});
</script>

<template>
  <v-dialog
    v-model="isOpen"
    data-testid="check-asset-panel"
    class="check-asset-panel-dialog"
    transition="slide-x-reverse-transition"
    scroll-strategy="none"
    @keydown.escape="closePanel"
    @after-leave="onAfterLeave">
    <v-card
      class="check-asset-panel d-flex flex-column bg-surface rounded-0 border-s border-color-line-border"
      flat>
      <AssetCheckPanelHeader
        :is-check-mode="isCheckMode"
        :is-loading="isLoading"
        :title="headerTitle"
        @close="closePanel" />

      <template v-if="!isCheckMode">
        <AssetCheckPanelLocationBar
          :is-check-mode="isCheckMode"
          :spot-code="spotCode"
          :area-name="areaName"
          :company-name="companyName"
          :is-hooked="isHooked"
          @change-location="openSpotPicker" />

        <div class="pa-4 bg--color-neutral-10">
          <div
            v-if="isLatestCheckMissing"
            class="d-flex align-center justify-center gap-2 pa-3 rounded border border-color-error-60 bg--color-neutral-0"
            data-testid="check-asset-panel-missing-banner">
            <v-icon size="20" color="error" icon="mdi-map-marker-remove-variant" />
            <span class="typography-body-md text--color-error-60">
              {{ t('assetCheckWizard.step1.missingFromYard') }}
            </span>
          </div>

          <AssetCheckPanelCheckSummary
            v-else
            :asset-id="assetId"
            :check-asset-containers="checkAssetContainers" />
        </div>
      </template>

      <template v-else>
        <AssetCheckWizardStepper :step="step" :subtitles="stepperSubtitles" />

        <div class="d-flex flex-grow-0 border-b border-color-line-border bg--color-neutral-10">
          <div class="flex-fill d-flex align-center justify-center gap-2 pa-3">
            <v-icon size="18" color="neutral-90" class="flex-shrink-0" icon="mdi-truck-trailer" />
            <span class="typography-body-md-bold text-truncate">{{ headerTitle }}</span>
          </div>
          <v-divider vertical />
          <div class="flex-fill d-flex align-center justify-center gap-2 pa-3">
            <v-icon
              size="18"
              color="neutral-90"
              class="flex-shrink-0"
              icon="mdi-map-marker-outline" />
            <span class="typography-body-md text-truncate">
              <template v-if="areaName && spotCode">
                {{ areaName }} –
                <strong>{{ spotCode }}</strong>
              </template>
              <template v-else>{{ spotCode || areaName || PRESENTER_VALUE_FALLBACK }}</template>
            </span>
          </div>
        </div>
      </template>

      <div class="flex-grow-1 overflow-y-auto">
        <div v-if="isLoading" class="pa-4 d-flex flex-column gap-4">
          <v-card variant="flat" border>
            <v-skeleton-loader type="sentences" class="bg-transparent" />
          </v-card>
          <v-card variant="flat" border>
            <v-skeleton-loader type="article" class="bg-transparent" />
          </v-card>
        </div>

        <AssetCheckPanelDetails v-else-if="!isCheckMode" :asset-id="assetId" :asset-data="asset" />

        <template v-else>
          <div v-if="isReassigningSpot" class="mt-4 pa-8 d-flex align-center justify-center gap-3">
            <v-progress-circular indeterminate size="24" color="primary" />
            <span class="typography-body-sm text-high-emphasis">
              {{ t('assetCheckWizard.reassigning') }}
            </span>
          </div>

          <AssetCheckWizardStep1
            v-else-if="step === WIZARD_STEPS.LOCATION"
            class="mt-4"
            :is-mutating="isMutatingStep(WIZARD_STEPS.LOCATION)"
            :error-message="step1ErrorMessage"
            :confirm-disabled="confirmDisabled"
            @submit-missing="submitMissing"
            @open-spot-picker="openSpotPicker"
            @confirm-location="confirmLocation" />

          <AssetCheckWizardStep2
            v-else-if="step === WIZARD_STEPS.STATUS"
            class="mt-4"
            :options="statusOptions"
            :selected-status="selectedStatus"
            :is-mutating="isMutatingStep(WIZARD_STEPS.STATUS)"
            :error-message="step2ErrorMessage"
            @select="selectStatus" />

          <AssetCheckWizardStep3
            v-else-if="step === WIZARD_STEPS.STATE"
            class="mt-4"
            :options="stateOptions"
            :selected-state="selectedState"
            :is-mutating="isMutatingStep(WIZARD_STEPS.STATE)"
            :error-message="step3ErrorMessage"
            @select="selectState" />
        </template>
      </div>

      <AssetCheckPanelFooter
        :is-check-mode="isCheckMode"
        :has-existing-check="hasExistingCheck"
        :task-completed="isTaskCompleted"
        @enter-check="enterCheckMode" />
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
@use '@/styles/partials/side-panel' as *;

.check-asset-panel {
  @include side-panel-card;
}

.check-asset-panel-dialog :deep(.v-overlay__content) {
  @include side-panel-dialog-overlay;
}
</style>
